import { useLayoutEffect, useRef } from 'react';

const dialogs: HTMLElement[] = [];
let previousOverflow = '';
let previousOverflowPriority = '';
const focusableSelector =
  'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]';

function focusable(dialog: HTMLElement) {
  return [...dialog.querySelectorAll<HTMLElement>(focusableSelector)].filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.matches(':disabled') &&
      !element.closest('[inert]') &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== 'hidden',
  );
}

/** Only the top dialog handles keys; cleanup restores focus without racing a new dialog. */
export function useDialogFocus(
  onClose: () => void,
  options: { initialFocus?: () => HTMLElement | null; enabled?: boolean } = {},
) {
  const ref = useRef<HTMLElement>(null);
  const latest = useRef({ onClose, initialFocus: options.initialFocus });
  latest.current = { onClose, initialFocus: options.initialFocus };
  const enabled = options.enabled ?? true;

  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!enabled || !dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (dialogs.length === 0) {
      previousOverflow = document.body.style.getPropertyValue('overflow');
      previousOverflowPriority = document.body.style.getPropertyPriority('overflow');
      document.body.style.setProperty('overflow', 'hidden');
    }
    dialogs.push(dialog);
    const isTop = () => dialogs.at(-1) === dialog;
    const focusFirst = () => (focusable(dialog)[0] ?? dialog).focus({ preventScroll: true });
    const initial = latest.current.initialFocus?.();
    (initial ?? focusable(dialog)[0] ?? dialog).focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTop()) return;
      if (event.key === 'Escape' && !event.isComposing) {
        event.preventDefault();
        event.stopPropagation();
        latest.current.onClose();
      }
      if (event.key !== 'Tab') return;
      const targets = focusable(dialog);
      const first = targets[0];
      const last = targets.at(-1);
      const active = document.activeElement;
      if (!first || !last) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
      } else if (event.shiftKey && (active === first || !targets.includes(active as HTMLElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !targets.includes(active as HTMLElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    const onFocus = (event: FocusEvent) => {
      if (isTop() && !dialog.contains(event.target as Node)) focusFirst();
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocus);
      const wasTop = isTop();
      const index = dialogs.lastIndexOf(dialog);
      if (index !== -1) dialogs.splice(index, 1);
      if (dialogs.length === 0) {
        if (previousOverflow) {
          document.body.style.setProperty('overflow', previousOverflow, previousOverflowPriority);
        } else {
          document.body.style.removeProperty('overflow');
        }
      }
      const parent = dialogs.at(-1);
      if (wasTop && opener?.isConnected && (!parent || parent.contains(opener))) {
        opener.focus({ preventScroll: true });
      } else if (wasTop && parent) {
        (focusable(parent)[0] ?? parent).focus({ preventScroll: true });
      }
    };
  }, [enabled]);
  return ref;
}
